import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "../utils/axios";
import { AuthContext } from "../context/AuthContext";
import "./styles/CarbonSavings.css";

// charts
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";
ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend, Title);

/* ----------------------------- helpers ----------------------------- */
const niceMax = (v) => {
  const steps = [0.5, 1, 2, 5, 10, 20, 50, 100, 200, 500, 1000];
  const abs = Math.max(0.0001, v);
  for (const s of steps) if (abs <= s) return s;
  const p = Math.pow(10, Math.ceil(Math.log10(abs)));
  return p;
};

const ddmmyyyy = (d) =>
  `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;

const toNum = (v) => {
  if (v == null) return 0;
  if (typeof v === "object" && v.$numberDecimal) return parseFloat(v.$numberDecimal) || 0;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

// Convert between backend format (DD/MM/YYYY) and <input type="date"> (YYYY-MM-DD)
const toInputDate = (ddmmyyyyStr) => {
  const [d, m, y] = (ddmmyyyyStr || "").split("/").map(Number);
  if (!d || !m || !y) return "";
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};
const fromInputDate = (yyyy_mm_dd) => {
  const [y, m, d] = (yyyy_mm_dd || "").split("-").map(Number);
  if (!y || !m || !d) return "";
  return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
};

/* ---------------------------- component ---------------------------- */
export default function CarbonSavingsPanel() {
  const { user, token } = useContext(AuthContext);

  // master site & dates
  const [masterSites, setMasterSites] = useState([]);
  const [selected, setSelected] = useState(""); // masterSiteId
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return ddmmyyyy(d);
  });
  const [to, setTo] = useState(() => ddmmyyyy(new Date()));

  // config knobs (editable)
  const [kWhPerM3, setKWhPerM3] = useState(1.2); // kWh per 1 m3 (1000 L)
  const [gridEF, setGridEF] = useState(0.7); // kg CO2e per kWh (typical India grid)
  const [treeKgPerYear, setTreeKgPerYear] = useState(21); // kg CO2/yr per tree (approx)

  // data
  const [rows, setRows] = useState([]); // [{ site, totalWaterDispensed, totalAmountCollected }]
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  useEffect(() => {
    if (user) fetchMasterSites();
  }, [user]); // eslint-disable-line

  /* ------------------------ data fetch ------------------------- */
  const fetchMasterSites = async () => {
    try {
      const res = await axios.get("/mastersites", authHeader);
      const filtered =
        user?.role === "admin"
          ? res.data
          : res.data.filter((ms) => (user?.masterSiteAccess || []).includes(ms._id));
      setMasterSites(filtered);

      // Auto-select first master site if none selected yet
      if (!selected && filtered.length > 0) {
        setSelected(filtered[0]._id);
      }
    } catch (e) {
      console.error(e);
      setErr("Failed to load master sites");
    }
  };

  // fetch totals for range using your working route
  const fetchTotals = async () => {
    if (!selected || !from || !to) return;
    setLoading(true);
    setErr("");
    try {
      const { data } = await axios.get(
        `/graphtotal/compare/master/${selected}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
        authHeader
      );
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || "Failed to load range totals");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // fetch when site changes
  useEffect(() => {
    if (selected) fetchTotals();
  }, [selected]); // eslint-disable-line

  // fetch when date changes (debounced)
  useEffect(() => {
    const t = setTimeout(fetchTotals, 250);
    return () => clearTimeout(t);
  }, [from, to]); // eslint-disable-line

  /* ------------------------ derived metrics ------------------------ */
  const liters = useMemo(() => rows.reduce((sum, r) => sum + toNum(r.totalWaterDispensed), 0), [rows]);
  const m3 = liters / 1000;
  const kWhSolarUsed = m3 * kWhPerM3;
  const kgCO2eAvoided = kWhSolarUsed * gridEF;
  const tCO2eAvoided = kgCO2eAvoided / 1000;
  const treesEquivalent = treeKgPerYear > 0 ? kgCO2eAvoided / treeKgPerYear : 0;

  // per-site bar charts
  const perSiteData = useMemo(() => {
    const labels = rows.map((r) => r.site || "Unknown");
    const kwhVals = rows.map((r) => (toNum(r.totalWaterDispensed) / 1000) * kWhPerM3);
    const co2Vals = kwhVals.map((k) => k * gridEF);
    return {
      kwhChart: {
        labels,
        datasets: [
          {
            label: "Energy (kWh) from Solar",
            data: kwhVals,
            backgroundColor: "rgba(0, 222, 255, 0.8)",
            borderColor: "#00eaff",
            borderWidth: 1.5,
            hoverBackgroundColor: "rgba(0, 222, 255, 1)",
          },
        ],
      },
      co2Chart: {
        labels,
        datasets: [
          {
            label: "CO₂e Avoided (kg)",
            data: co2Vals,
            backgroundColor: "rgba(255, 196, 0, 0.8)",
            borderColor: "#ffd54f",
            borderWidth: 1.5,
            hoverBackgroundColor: "rgba(255, 196, 0, 1)",
          },
        ],
      },
    };
  }, [rows, kWhPerM3, gridEF]);

  const barOptions = (title) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: { display: true, text: title, color: "#ffffff", font: { size: 15, weight: "600" } },
      legend: { display: true, position: "top", labels: { color: "#c8f4ff" } },
      tooltip: {
        callbacks: { label: (ctx) => ` ${ctx.dataset.label}: ${ctx.formattedValue}` },
        backgroundColor: "rgba(0,0,0,0.7)",
        titleColor: "#fff",
        bodyColor: "#fff",
      },
    },
    scales: {
      x: { ticks: { color: "#aef0ff", font: { weight: "600" } }, grid: { color: "rgba(255,255,255,0.1)" } },
      y: {
        ticks: { color: "#aef0ff", font: { weight: "600" } },
        grid: { color: "rgba(255,255,255,0.1)" },
        beginAtZero: true,
      },
    },
  });

  // donut gauge
  const gaugeMax = useMemo(() => niceMax(tCO2eAvoided), [tCO2eAvoided]);
  const donutData = useMemo(
    () => ({
      labels: ["CO₂e Avoided (t)", "Remainder"],
      datasets: [
        {
          data: [Math.max(0, tCO2eAvoided), Math.max(0.0001, gaugeMax - Math.max(0, tCO2eAvoided))],
          backgroundColor: ["rgba(0, 222, 255, 0.9)", "rgba(255,255,255,0.12)"],
          borderColor: ["#00eaff", "rgba(255,255,255,0.18)"],
          borderWidth: 1.5,
          hoverBackgroundColor: ["rgba(0, 222, 255, 1)", "rgba(255,255,255,0.18)"],
        },
      ],
    }),
    [tCO2eAvoided, gaugeMax]
  );

  /* ------------------------------- UI ------------------------------- */
  return (
    <div className="carbon-card">
      <div className="carbon-head">
        <div>
          <h3 className="carbon-title">Solar Carbon Savings</h3>
          <p className="carbon-sub">Estimate avoided emissions using your dispensed volume</p>
        </div>

        <div className="carbon-controls">
          <div className="ctrl">
            <label>Master Site</label>
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              onBlur={(e) => !e.target.value && masterSites[0]?._id && setSelected(masterSites[0]._id)}
            >
              {masterSites.map((m) => (
                <option key={m._id} value={m._id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>

          {/* Calendar pickers */}
          <div className="ctrl">
            <label>From</label>
            <input
              type="date"
              value={toInputDate(from)}
              onChange={(e) => setFrom(fromInputDate(e.target.value))}
              max={toInputDate(to)}
            />
          </div>
          <div className="ctrl">
            <label>To</label>
            <input
              type="date"
              value={toInputDate(to)}
              onChange={(e) => setTo(fromInputDate(e.target.value))}
              min={toInputDate(from)}
            />
          </div>

          {/* Quick ranges */}
          <div className="ctrl" style={{ display: "flex", gap: 6 }}>
            <button
              type="button"
              className="btn-mini"
              onClick={() => {
                const d = new Date();
                const toStr = ddmmyyyy(d);
                d.setDate(d.getDate() - 6);
                setFrom(ddmmyyyy(d));
                setTo(toStr);
              }}
              title="Last 7 days"
            >
              7d
            </button>
            <button
              type="button"
              className="btn-mini"
              onClick={() => {
                const d = new Date();
                const toStr = ddmmyyyy(d);
                d.setDate(d.getDate() - 29);
                setFrom(ddmmyyyy(d));
                setTo(toStr);
              }}
              title="Last 30 days"
            >
              30d
            </button>
            <button
              type="button"
              className="btn-mini"
              onClick={() => {
                const d = new Date();
                const toStr = ddmmyyyy(d);
                d.setDate(d.getDate() - 89);
                setFrom(ddmmyyyy(d));
                setTo(toStr);
              }}
              title="Last 90 days"
            >
              90d
            </button>
          </div>

          <button className="btn-refresh" onClick={fetchTotals} disabled={loading}>
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="carbon-grid">
        {/* ring + headline numbers */}
        <div className="ring-wrap">
          <div className="ring">
            <Doughnut
              key={`${selected}-${from}-${to}-${kWhPerM3}-${gridEF}-${liters.toFixed(0)}`}
              data={donutData}
              options={{
                cutout: "70%",
                plugins: { legend: { display: false }, title: { display: false } },
                animation: { duration: 450, easing: "easeOutQuart" },
              }}
            />
            <div className="ring-center">
              <div className="ring-num">{tCO2eAvoided.toFixed(2)}</div>
              <div className="ring-label">t CO₂e avoided</div>
              <div style={{ fontSize: 11, opacity: 0.75, marginTop: 4 }}>out of ~{gaugeMax} t</div>
            </div>
          </div>

          <div className="quick-stats">
            <div className="stat">
              <div className="k">Dispensed</div>
              <div className="v">{liters.toFixed(0)} L</div>
            </div>
            <div className="stat">
              <div className="k">Energy Used</div>
              <div className="v">{kWhSolarUsed.toFixed(0)} kWh</div>
            </div>
            <div className="stat">
              <div className="k">Trees (equiv.)</div>
              <div className="v">{Math.round(treesEquivalent).toLocaleString()}</div>
            </div>
          </div>
        </div>

        {/* per-site bars */}
        <div className="bars">
          <div className="bar-card">
            <Bar data={perSiteData.kwhChart} options={barOptions("Energy from Solar by Site (kWh)")} />
          </div>
          <div className="bar-card">
            <Bar data={perSiteData.co2Chart} options={barOptions("CO₂e Avoided by Site (kg)")} />
          </div>
        </div>

        {/* settings */}
        <div className="settings">
          <h4>Assumptions</h4>
          <div className="settings-grid">
            <div>
              <label>Energy Intensity (kWh / m³)</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={kWhPerM3}
                onChange={(e) => setKWhPerM3(parseFloat(e.target.value) || 0)}
              />
              <small>1 m³ = 1000 L of dispensed water</small>
            </div>
            <div>
              <label>Grid Emission Factor (kg CO₂e / kWh)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={gridEF}
                onChange={(e) => setGridEF(parseFloat(e.target.value) || 0)}
              />
              <small>Adjust to your region/utility</small>
            </div>
            <div>
              <label>Tree Absorption (kg CO₂ / year)</label>
              <input
                type="number"
                min="1"
                step="1"
                value={treeKgPerYear}
                onChange={(e) => setTreeKgPerYear(parseFloat(e.target.value) || 0)}
              />
              <small>For equivalence display only</small>
            </div>
          </div>
          <p className="note">
            Note: This is an operational estimate. To publish externally, align factors with your auditor or use
            site-measured electricity and certified emission factors.
          </p>
        </div>
      </div>

      {err && <p className="error">{err}</p>}
    </div>
  );
}
